'use client';

import { c as _c } from "react/compiler-runtime";
import { type PinnedFactiii } from '@shared/all/domains/factiiis';
import useUser from '@shared/utils/hooks/useUser';
import { Plus } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { NewPostModal } from '../posts/NewPost/NewPostModal';
import ScrollableText from '../UI/ScrollableText';
import Avatar from './Avatar';
import PinnedFactiiiModal from './PinnedFactiiisModal';
type Props = {
  spaceSlug?: string;
  factiiis: PinnedFactiii[];
  /** Profile owner: the modal gains search-to-pin, unpin and reordering. */
  canEdit?: boolean;
};
const PinnedFactiiis = t0 => {
  const $ = _c(36);
  const {
    spaceSlug,
    factiiis,
    canEdit
  } = t0;
  const {
    currentUser
  } = useUser();
  const router = useRouter();
  const [openNewPostModal, setOpenNewPostModal] = useState(false);
  const [selectedFactiii, setSelectedFactiii] = useState();
  const [expanded, setExpanded] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const maxVisibleAvatars = Math.floor((containerWidth - 32) / 70);
  let t1;
  let t2;
  if ($[0] !== selectedFactiii) {
    t1 = () => {
      if (selectedFactiii) {
        setOpenNewPostModal(true);
      }
    };
    t2 = [selectedFactiii];
    $[0] = selectedFactiii;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      return () => {
        resizeObserver.disconnect();
      };
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== currentUser.isAuthenticated || $[6] !== router) {
    t5 = factiii => {
      if (currentUser.isAuthenticated) {
        setSelectedFactiii(factiii);
      } else {
        router.push(`/fids/${factiii.id}`);
      }
    };
    $[5] = currentUser.isAuthenticated;
    $[6] = router;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleFactiiiClick = t5;
  let t6;
  if ($[8] !== factiiis || $[9] !== handleFactiiiClick || $[10] !== maxVisibleAvatars) {
    let t7;
    if ($[12] !== factiiis.length || $[13] !== handleFactiiiClick || $[14] !== maxVisibleAvatars) {
      t7 = (factiii_0, index) => {
        const isVisible = index < maxVisibleAvatars;
        return isVisible ? <div key={factiii_0.id}><div className="m-1 flex w-auto max-w-[50px] cursor-pointer flex-col items-center rounded-[10px] p-2 transition-all duration-300 hover:max-w-[128px] hover:scale-110 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]" onClick={() => handleFactiiiClick(factiii_0)}><Avatar factiiiAvatarKey={factiii_0.avatarKey} factiiiId={factiii_0.id} size={32} /><ScrollableText center={true} className="w-full text-xs">{factiii_0.name}</ScrollableText></div></div> : index === maxVisibleAvatars ? <div key="more" className="mb-3 flex size-8 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-background-secondary text-xs font-emphasis transition-transform duration-200 hover:scale-[1.05]" onClick={() => setExpanded(true)}>+{factiiis.length - maxVisibleAvatars}</div> : null;
      };
      $[12] = factiiis.length;
      $[13] = handleFactiiiClick;
      $[14] = maxVisibleAvatars;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t6 = factiiis.map(t7);
    $[8] = factiiis;
    $[9] = handleFactiiiClick;
    $[10] = maxVisibleAvatars;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[16] !== canEdit || $[17] !== factiiis.length || $[18] !== maxVisibleAvatars) {
    t7 = canEdit && factiiis.length <= maxVisibleAvatars && <button type="button" aria-label="Manage pinned factiiis" className="mb-3 flex size-8 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-background-secondary text-xs font-emphasis transition-transform duration-200 hover:scale-[1.05]" onClick={() => setExpanded(true)}><Plus size={16} /></button>;
    $[16] = canEdit;
    $[17] = factiiis.length;
    $[18] = maxVisibleAvatars;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <div className="space-y-2"><div className="anim-enter relative mx-4 flex h-10 items-center justify-between md:justify-start">{t6}{t7}</div></div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== canEdit || $[24] !== expanded || $[25] !== factiiis || $[26] !== handleFactiiiClick) {
    t9 = expanded && <PinnedFactiiiModal factiiis={factiiis} canEdit={canEdit} closeHandler={factiii_1 => {
      setExpanded(false);
      if (factiii_1) {
        handleFactiiiClick(factiii_1);
      }
    }} />;
    $[23] = canEdit;
    $[24] = expanded;
    $[25] = factiiis;
    $[26] = handleFactiiiClick;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== openNewPostModal || $[29] !== selectedFactiii || $[30] !== spaceSlug) {
    t10 = openNewPostModal && selectedFactiii && <NewPostModal spaceSlug={spaceSlug ? spaceSlug : selectedFactiii.spaceSlug} pinnedFactiii={selectedFactiii} closeHandler={() => setSelectedFactiii(null)} />;
    $[28] = openNewPostModal;
    $[29] = selectedFactiii;
    $[30] = spaceSlug;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t8 || $[34] !== t9) {
    t11 = <div ref={containerRef} className="space-y-4">{t8}{t9}{t10}</div>;
    $[32] = t10;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};
export default PinnedFactiiis;